import { createTheme } from '@mui/material/styles';

/**
 * MUI Theme configured with MietClub branding colors
 *
 * Note: MUI's palette system requires hex color values and doesn't support CSS variables.
 * Color values in the palette section are hardcoded from shared/ui/styles.css.
 * CSS variables are used in component styleOverrides where they work properly.
 */

// Helper function to get CSS variable value (for use in styleOverrides)
const getCSSVar = (varName: string) => `var(${varName})`;

export const muiTheme = createTheme({
  palette: {
    primary: {
      main: '#3d98ff',
      light: '#6bb0ff',
      dark: '#2680e8',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#21b382', // green for actions
      light: '#2dd396',
      dark: '#1a9068',
      contrastText: '#ffffff',
    },
    error: {
      main: '#d32f2f',
      light: '#ef5350',
    },
    warning: {
      main: '#ed6c02',
      light: '#ff9800',
    },
    success: {
      main: '#2e7d32',
      light: '#4caf50',
    },
    info: {
      main: '#0288d1',
    },
    text: {
      primary: '#1e4b80',
      secondary: '#616161',
      disabled: '#9e9e9e',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: getCSSVar('--font-family'),
    fontSize: 16,
    h1: {
      fontWeight: getCSSVar('--font-weight-bold'),
      fontSize: getCSSVar('--font-size-4xl'),
      lineHeight: 1.2,
      color: getCSSVar('--color-text-primary'),
    },
    h2: {
      fontWeight: getCSSVar('--font-weight-semibold'),
      fontSize: getCSSVar('--font-size-3xl'),
      lineHeight: 1.3,
      color: getCSSVar('--color-text-primary'),
    },
    h3: {
      fontWeight: getCSSVar('--font-weight-semibold'),
      fontSize: getCSSVar('--font-size-2xl'),
      lineHeight: 1.4,
      color: getCSSVar('--color-text-primary'),
    },
    h4: {
      fontWeight: getCSSVar('--font-weight-semibold'),
      fontSize: getCSSVar('--font-size-xl'),
      lineHeight: 1.4,
      color: getCSSVar('--color-text-primary'),
    },
    h5: {
      fontWeight: getCSSVar('--font-weight-medium'),
      fontSize: getCSSVar('--font-size-lg'),
      lineHeight: 1.5,
      color: getCSSVar('--color-text-primary'),
    },
    h6: {
      fontWeight: getCSSVar('--font-weight-medium'),
      fontSize: getCSSVar('--font-size-base'),
      lineHeight: 1.5,
      color: getCSSVar('--color-text-primary'),
    },
    button: {
      textTransform: 'none',
      fontWeight: getCSSVar('--font-weight-medium'),
    },
  },
  shape: {
    borderRadius: 8, // var(--radius-md)
  },
  shadows: [
    'none',
    getCSSVar('--shadow-sm'), // '0 2px 4px rgba(0, 0, 0, 0.05)'
    getCSSVar('--shadow-md'), // '0 4px 8px rgba(0, 0, 0, 0.08)'
    getCSSVar('--shadow-lg'), // '0 8px 16px rgba(0, 0, 0, 0.12)'
    getCSSVar('--shadow-xl'), // '0 16px 32px rgba(0, 0, 0, 0.15)'
    getCSSVar('--shadow-sm'),
    getCSSVar('--shadow-md'),
    getCSSVar('--shadow-lg'),
    getCSSVar('--shadow-xl'),
    getCSSVar('--shadow-sm'),
    getCSSVar('--shadow-md'),
    getCSSVar('--shadow-lg'),
    getCSSVar('--shadow-xl'),
    getCSSVar('--shadow-sm'),
    getCSSVar('--shadow-md'),
    getCSSVar('--shadow-lg'),
    getCSSVar('--shadow-xl'),
    getCSSVar('--shadow-sm'),
    getCSSVar('--shadow-md'),
    getCSSVar('--shadow-lg'),
    getCSSVar('--shadow-xl'),
    getCSSVar('--shadow-sm'),
    getCSSVar('--shadow-md'),
    getCSSVar('--shadow-lg'),
    getCSSVar('--shadow-xl'),
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: getCSSVar('--radius-md'),
          padding: '10px 24px',
          fontSize: getCSSVar('--font-size-base'),
          fontWeight: getCSSVar('--font-weight-medium'),
          transition: `all ${getCSSVar('--transition-base')}`,
          textTransform: 'none',
        },
        contained: {
          boxShadow: getCSSVar('--shadow-sm'),
          '&:hover': {
            boxShadow: getCSSVar('--shadow-md'),
          },
        },
        containedPrimary: {
          background: getCSSVar('--color-action'),
          color: getCSSVar('--color-white'),
          '&:hover': {
            background: getCSSVar('--color-action-dark'),
          },
        },
        containedSecondary: {
          background: getCSSVar('--color-primary'),
          color: getCSSVar('--color-white'),
          '&:hover': {
            background: getCSSVar('--color-primary-dark'),
          },
        },
        outlined: {
          borderColor: getCSSVar('--color-action'),
          color: getCSSVar('--color-action'),
          '&:hover': {
            backgroundColor: 'rgba(33, 179, 130, 0.1)',
            borderColor: getCSSVar('--color-action-dark'),
          },
        },
        outlinedSecondary: {
          borderColor: getCSSVar('--color-primary'),
          color: getCSSVar('--color-primary'),
          '&:hover': {
            backgroundColor: 'rgba(61, 152, 255, 0.1)',
            borderColor: getCSSVar('--color-primary-dark'),
          },
        },
        text: {
          color: getCSSVar('--color-action'),
          '&:hover': {
            backgroundColor: 'rgba(33, 179, 130, 0.1)',
          },
        },
        sizeLarge: {
          padding: '12px 32px',
          fontSize: getCSSVar('--font-size-lg'),
        },
        sizeSmall: {
          padding: '6px 16px',
          fontSize: getCSSVar('--font-size-sm'),
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: getCSSVar('--radius-md'),
            '& fieldset': {
              borderColor: getCSSVar('--color-gray-300'),
              transition: `border-color ${getCSSVar('--transition-base')}`,
            },
            '&:hover fieldset': {
              borderColor: getCSSVar('--color-primary'),
            },
            '&.Mui-focused fieldset': {
              borderColor: getCSSVar('--color-primary'),
              borderWidth: '2px',
            },
          },
          '& .MuiInputLabel-root': {
            color: getCSSVar('--color-text-secondary'),
            fontWeight: getCSSVar('--font-weight-medium'),
            '&.Mui-focused': {
              color: getCSSVar('--color-primary'),
            },
          },
          '& .MuiFormHelperText-root': {
            fontSize: getCSSVar('--font-size-sm'),
            marginTop: getCSSVar('--spacing-xs'),
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: getCSSVar('--radius-md'),
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: getCSSVar('--color-gray-300'),
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: getCSSVar('--color-primary'),
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: getCSSVar('--color-primary'),
            borderWidth: '2px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: getCSSVar('--radius-lg'),
          boxShadow: getCSSVar('--shadow-md'),
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: getCSSVar('--font-weight-medium'),
          color: getCSSVar('--color-text-secondary'),
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: getCSSVar('--color-action'),
          textDecoration: 'none',
          transition: `color ${getCSSVar('--transition-fast')}`,
          '&:hover': {
            color: getCSSVar('--color-action-light'),
            textDecoration: 'underline',
          },
        },
      },
    },
  },
});
